import * as R from 'Router/routes';
import { Application } from './useApplication';
import { axiosInstance } from 'utils';
import { COOKIE_MODAL_HIDDEN_NAME } from 'components/Modals/CardMissingModal/CardMissingModal';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { ERROR_LOGIN_MESSAGE_QUERY_PARAM } from '../../customAnalytics/constants';
import { FieldError } from 'utils/ValidationError';
import {
  FINGERPRINT_CONSTANTS_KEYS,
  useFingerprint,
} from 'hooks/useFingerprint';
import { getCalculatorUrl, getLoginUrl, getProcessEnvUrl } from 'utils/getUrl';
import { getClientProductNumber } from 'utils/getProductNumber';
import {
  getShiftedDatetime,
  getShiftedDatetimeHeader,
  SHIFTED_DATETIME,
} from 'utils/shiftedDateTime';
import { isValidApplication } from 'utils/applicationUtility';
import { PRODUCT_NUMBER_PARAM } from 'utils/constants';
import { SOLD_DEBT_MODAL_HIDDEN } from 'components/Modals/ModalSoldDebt/ModalSoldDebt';
import { usePhone } from 'hooks/usePhone';
import { useSoldDebt } from 'hooks/useSoldDebt';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import isLogged from 'utils/isLogged';
import useClient, { Client } from './useClient';

type Props = {
  children: ReactNode;
};

export type LoginResponse = {
  fromTemporaryPassword?: boolean;
  accessVerificationRequired?: boolean;
  authorizationToken: string | null;
};

type CrossLoginParams = {
  invalidateSession?: boolean;
  application?: Application | null;
  client?: Client | undefined | null;
};

type AuthContextType = {
  login: (email: string, password: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  crossLoginRedirect: (params?: CrossLoginParams) => void;
  crossLoginAuth: (token: string) => Promise<LoginResponse>;
  clearStorage: () => void;
  loginAuth: string;
  setLoginAuth: (token: string) => void;
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

const useAuth = (): AuthContextType => useContext(AuthContext);

const AuthProvider = ({ children }: Props): JSX.Element => {
  const [loginAuth, setLoginAuth] = useState('');
  const {
    clearFingerprintSessionKeys,
    getDeviceId,
    getCodeId,
    setVerificationSessionId,
  } = useFingerprint();
  const { client } = useClient();
  const { t } = useTranslation();

  const { getSoldDebtMessageFromErrors } = useSoldDebt({});
  const { phone, phoneLink } = usePhone();

  const login = useCallback(async (email: string, password: string) => {
    const path = '/token/basic';
    const url = `${getProcessEnvUrl('WEB_API')}${path}`;

    try {
      const encodedCredentials = btoa(`${email}:${password}`);

      sessionStorage.setItem('email', email);

      const deviceId = await getDeviceId();

      const temporalToken = `Basic ${encodedCredentials}`;

      const headers = {
        ...getShiftedDatetimeHeader(),
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: temporalToken,
        [FINGERPRINT_CONSTANTS_KEYS.deviceId]: deviceId,
      };

      const response = await axiosInstance.post(path, undefined, {
        headers,
      });

      const data = response.data;

      const authorizationToken = data.token
        ? btoa(`${data.username}:${data.token}`)
        : null;

      sessionStorage.setItem(
        'fromTemporaryPassword',
        Boolean(data.fromTemporaryPassword).toString(),
      );

      if (authorizationToken) {
        sessionStorage.setItem('token', authorizationToken);
        clearFingerprintSessionKeys();
      }

      const accessVerificationRequired = Boolean(data.verificationId);

      if (accessVerificationRequired) {
        sessionStorage.setItem('maskedPhoneNumber', data.maskedPhoneNumber);
      }

      if (data.verificationId) {
        setVerificationSessionId(data.verificationId);
        await getCodeId();
      }

      setLoginAuth(encodedCredentials || authorizationToken || '');

      return {
        accessVerificationRequired,
        authorizationToken,
        fromTemporaryPassword: Boolean(data.fromTemporaryPassword),
      };
    } catch (e) {
      if (!axios.isAxiosError(e) && (e as any).status !== 401) {
        datadogLogs.logger.error(`request: ${path} response parse`, {
          request_url: url,
          error: e,
        });
        throw e;
      }

      let message;
      const status = (e as any).status;
      switch (status) {
        case 401:
          message = t('error:validation.unauthorized');
          break;
        case 403:
          const errors = e.response?.data.errors as FieldError[];
          const messageSoldDebt = getSoldDebtMessageFromErrors(errors);
          message =
            messageSoldDebt ||
            t('error:validation.session', { phone, phoneLink });
          break;
        default:
          message =
            e.message && e.message !== 'Failed to fetch'
              ? e.message
              : t('error:generic');
      }

      throw Error(message);
    }
  }, []);

  const clearStorage = useCallback(() => {
    sessionStorage.removeItem('fromTemporaryPassword');
    sessionStorage.removeItem('maskedPhoneNumber');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem(COOKIE_MODAL_HIDDEN_NAME);
    sessionStorage.removeItem(SOLD_DEBT_MODAL_HIDDEN);
  }, []);

  const logout = useCallback(
    async (redirect = true) => {
      try {
        if (isLogged()) {
          sessionStorage.removeItem('discountBannerShowed');

          await axiosInstance.delete('/token/invalidate');

          clearStorage();
        }
      } catch (e) {
        throw e;
      } finally {
        // If invalidate request fails for some reason, at least remove token
        clearStorage();

        if (redirect) {
          const loginUrl = getLoginUrl();
          const homeURL =
            import.meta.env.NODE_ENV === 'development'
              ? loginUrl
              : getCalculatorUrl() || loginUrl;

          window.location.assign(homeURL);
        }
      }
    },
    [clearStorage],
  );

  const crossLoginAuth = useCallback(async (oneTimeToken: string) => {
    const response = await axiosInstance.post('/token/one-time', {
      oneTimeToken,
    });

    const { username, token, fromTemporaryPassword } = response.data;

    const authorizationToken = btoa(`${username}:${token}`);

    sessionStorage.setItem('token', authorizationToken);
    sessionStorage.setItem('fromTemporaryPassword', fromTemporaryPassword);

    return {
      authorizationToken,
      fromTemporaryPassword,
    };
  }, []);

  const crossLoginRedirect = useCallback(
    async (params: CrossLoginParams = {}) => {
      const { application, invalidateSession = true } = params;

      try {
        // Use open application's product number directly; otherwise derive it from availableProductsInfo by the kind stored at login
        const clientProductName = isValidApplication(application, client)
          ? application?.productNumber
          : getClientProductNumber(params.client?.availableProductsInfo);

        const url =
          clientProductName === '6'
            ? getProcessEnvUrl('VIVUSONLINE_REGISTRATION_URL')
            : getProcessEnvUrl('VIVUS_REGISTRATION_URL');

        const response = await axiosInstance.post(
          '/client/security/one-time-token',
        );

        const { oneTimeToken } = response.data;

        if (invalidateSession) {
          await logout(false);
        }

        const paramsUrl = new URLSearchParams(
          decodeURIComponent(window.location.search),
        );
        // remove these two query params because they are obsolete.
        paramsUrl.delete('token');

        const queryString = paramsUrl.size ? `&${paramsUrl.toString()}` : '';

        const productNameQuery = sessionStorage.getItem(PRODUCT_NUMBER_PARAM)
          ? `&productNumber=${sessionStorage.getItem(PRODUCT_NUMBER_PARAM)}`
          : '';

        const sessionTime = getShiftedDatetime();
        const sessionTimeNumber =
          sessionTime && new Date(sessionTime).getTime();
        const timeQueryParam = sessionTimeNumber
          ? `&${SHIFTED_DATETIME}=${sessionTimeNumber}`
          : '';

        const searchParams = `token=${oneTimeToken}${queryString}${productNameQuery}${timeQueryParam}`;

        const redirectURL = `${url}/one-time-token-login?${searchParams}`;

        window.location.assign(redirectURL);
      } catch (e) {
        const loginUrlError = `${R.LOGIN}?${ERROR_LOGIN_MESSAGE_QUERY_PARAM}=register`;
        if (
          window.location.pathname + window.location.search !==
          loginUrlError
        ) {
          window.location.assign(loginUrlError);
        }
      }
    },
    [logout, client],
  );

  const profileContextValue = useMemo(
    () => ({
      login,
      logout,
      crossLoginRedirect,
      crossLoginAuth,
      clearStorage,
      loginAuth,
      setLoginAuth,
    }),
    [
      login,
      logout,
      crossLoginRedirect,
      crossLoginAuth,
      clearStorage,
      loginAuth,
      setLoginAuth,
    ],
  );

  return (
    <AuthContext.Provider value={profileContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export { useAuth as default, AuthProvider };
